import { useEffect, useState } from 'react';
import { api, type CategoryNavigation } from '../api';

/**
 * Rozcestnik kategorii nad mrizkou zbozi.
 *
 * PROC vznikl: puvodni pas chipu vypisoval `/api/shop/categories` naplocho,
 * tedy KAZDOU kategorii s viditelnym zbozim. Pri nasich 82 kategoriich to byla
 * dlouha, ale jeste ovladatelna rada. Strom dodavatele ma 204 kategorii, z toho
 * 109 na treti urovni — plochy pas by na mobilu znamenal stovku chipu za sebou
 * a zadnou informaci o tom, co pod co patri.
 *
 * Misto toho se ukazuje vzdycky JEN JEDNA uroven:
 *   - bez vybrane kategorie hlavni kategorie (`/api/shop/category-tree`),
 *   - ve vybrane kategorii jeji prime podkategorie s poctem zbozi,
 *   - a nad tim drobeckova cesta zpatky ke koreni.
 *
 * Tim se da proklikat i ctvrta uroven, na kterou navigacni pruh v hlavicce
 * nedosahne (umi top-level + jednu uroven) a na mobilu, kde mega-menu neni,
 * uz vubec. Pocet u dlazdice je za celou vetev, takze sedi s tim, co se po
 * kliknuti opravdu vypise.
 *
 * Kdyz se rozcestnik nenacte, vykresli se jen „Vše" + hlavni kategorie:
 * filtr v URL funguje dal, jen se z nej hur odchazi.
 */
export function CategoryNav({
  active,
  onPick,
}: {
  readonly active: string;
  readonly onPick: (slug: string) => void;
}) {
  const [roots, setRoots] = useState<readonly { slug: string; name: string }[]>([]);
  const [navigation, setNavigation] = useState<CategoryNavigation | null>(null);

  useEffect(() => {
    let alive = true;
    void api
      .listCategoryTree()
      .then((result) => {
        if (alive) setRoots(result.categories.map((c) => ({ slug: c.slug, name: c.name })));
      })
      .catch(() => {
        if (alive) setRoots([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (active === '') {
      setNavigation(null);
      return;
    }
    let alive = true;
    void api
      .categoryNavigation(active)
      .then((result) => {
        if (alive) setNavigation(result);
      })
      // Neznamy slug v URL (404) neni chyba k zobrazeni – mrizka stejne
      // vypise „nic jsme nenasli" a drobecky by tu nemely co ukazat.
      .catch(() => {
        if (alive) setNavigation(null);
      });
    return () => {
      alive = false;
    };
  }, [active]);

  if (roots.length === 0 && navigation === null) return null;

  // Ve vybrane kategorii nabizime jeji potomky; v listu stromu uz neni kam jit
  // hloub, takze zustane jen drobeckova cesta.
  const options =
    active === ''
      ? roots.map((root) => ({ slug: root.slug, name: root.name, productCount: null }))
      : (navigation?.children ?? []).map((child) => ({
          slug: child.slug,
          name: child.name,
          productCount: child.productCount,
        }));

  return (
    <div className="category-nav">
      {navigation !== null && (
        <nav className="category-crumbs" aria-label="Kde jsem">
          <button type="button" className="crumb" onClick={() => onPick('')}>
            Vše
          </button>
          {navigation.ancestors.map((ancestor) => (
            <button
              key={ancestor.slug}
              type="button"
              className="crumb"
              onClick={() => onPick(ancestor.slug)}
            >
              {ancestor.name}
            </button>
          ))}
          <span className="crumb current" aria-current="page">
            {navigation.category.name}
          </span>
        </nav>
      )}

      {options.length > 0 && (
        <div className="chips">
          {active !== '' && (
            <button type="button" className="chip" onClick={() => onPick('')}>
              Vše
            </button>
          )}
          {options.map((option) => (
            <button
              key={option.slug}
              type="button"
              className={active === option.slug ? 'chip active' : 'chip'}
              onClick={() => onPick(option.slug)}
            >
              {option.name}
              {option.productCount !== null && option.productCount > 0 && (
                <span className="chip-count">{option.productCount}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
